import type { MediaAsset, MediaDerivative } from '@/lib/api/types';

/**
 * Reading a media asset's generated sizes.
 *
 * ── The defect this exists to make unrepresentable (QA F-13) ────────────────
 *
 * `be-svet` generates a `thumb` (400px) and a `card` (800px) for every uploaded image,
 * stores them, and serves them in the asset payload. The console never used one: its type
 * declared `derivatives` as a `Record<string, …>` and both call sites read
 * `asset.derivatives?.thumb?.url ?? asset.url`, while the API sends an ARRAY of objects
 * each carrying its own `label`. `thumb` is not an index into an array, so the expression
 * was `undefined` for every asset in the library and the fallback served the full-size
 * original into every grid tile and every picker preview.
 *
 * Nothing threw and nothing logged — the optional chain and the `??` made the failure
 * silent, which is why it survived to QA. The type was wrong in two independent ways
 * (a map instead of a list, `width`/`height` instead of `w`/`h`), so a fixture typed from
 * it would have passed the broken code: the test beside this file feeds the REAL API
 * response instead.
 *
 * ── Why a helper rather than a corrected expression at each site ────────────
 *
 * Two call sites today (`MediaBrowser`, `MediaPickerHost`) and a third the moment a
 * picker gains a preview. `find(d => d.label === 'thumb')` is easy to write and just as
 * easy to write as `derivatives[0]`, which happens to be the thumb today and is the card
 * for any asset whose source was too small for a thumb (QA B-09's size bands). Naming the
 * label once is what keeps that from being discovered later.
 */

/** The sizes `be-svet` generates, largest last. `original` is not a derivative. */
export type DerivativeLabel = 'thumb' | 'card';

/**
 * The URL of one generated size, or `undefined` when the asset does not have it.
 *
 * An asset legitimately has no derivatives at all: a PDF, a video, an SVG, or an image
 * whose source was narrower than the smallest derivative width — `be-svet` stores `[]`
 * for all of them (QA B-09). The caller decides what to show instead, because the answer
 * differs: a grid tile falls back to the original, a print-quality download does not want
 * one at all.
 */
export function derivativeUrl(
  asset: Pick<MediaAsset, 'derivatives'> | undefined | null,
  label: DerivativeLabel,
): string | undefined {
  // `Array.isArray` rather than trusting the type: this value comes off the wire, and the
  // whole finding was a type that disagreed with what the server sends. An older row, a
  // partial payload or a JSON `null` degrades to "no derivative" instead of throwing in a
  // render.
  const derivatives: MediaDerivative[] = Array.isArray(asset?.derivatives) ? asset.derivatives : [];
  return derivatives.find((d) => d?.label === label)?.url || undefined;
}

/**
 * What to put in an `<img src>` for a thumbnail-sized preview: the thumb, then the card,
 * then the original.
 *
 * The card is in the chain deliberately. An image between the two widths gets a card and
 * no thumb, and 800px is still an order of magnitude less than a 4 MB original — so the
 * ladder degrades a step at a time rather than falling all the way back on the assets
 * where the saving matters most.
 */
export function previewUrl(asset: Pick<MediaAsset, 'derivatives' | 'url'>): string {
  return derivativeUrl(asset, 'thumb') ?? derivativeUrl(asset, 'card') ?? asset.url;
}
